export interface PatientFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth: string;
  ssnLast4: string;
  address: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

export const emptyPatientForm: PatientFormData = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  dateOfBirth: "",
  ssnLast4: "",
  address: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
};

export const validateName = (name: string): string => {
  if (!name.trim()) return "This field is required";
  if (name.includes(" ")) return "Name cannot contain spaces";
  if (!/^[a-zA-Z]+$/.test(name)) return "Name can only contain letters";
  return "";
};

export const validatePhoneNumber = (phone: string): string => {
  if (!phone.trim()) return "Phone number is required";
  const phoneRegex = /^\d{10}$/;
  if (!phoneRegex.test(phone)) {
    return "Please enter a valid 10-digit phone number (e.g., 1234567890)";
  }
  return "";
};

export const validateDateOfBirth = (dateString: string): string => {
  if (!dateString.trim()) return "Date of birth is required";
  
  // Parse date string manually to avoid timezone issues
  let date: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // YYYY-MM-DD format - parse manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateString);
  }
  
  if (Number.isNaN(date.getTime())) return "Please enter a valid date";

  const today = new Date();
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const birthdayHasOccurred = !(monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate()));
  const actualAge = birthdayHasOccurred ? age : age - 1;

  if (actualAge < 18) return "Customer must be at least 18 years old";
  return "";
};

export const validateSsnLast4 = (ssnLast4: string): string => {
  if (!ssnLast4.trim()) return ""; // optional field
  if (!/^\d{4}$/.test(ssnLast4)) return "Enter exactly 4 digits";
  return "";
};

export const validateCity = (city: string): string => {
  if (!city.trim()) return "City is required";
  if (!/^[a-zA-Z\s.,'-]+$/.test(city)) {
    return "City can only contain letters, spaces, periods, commas, apostrophes, and hyphens";
  }
  if (city.trim().length < 2) return "Please enter a valid US city name";
  return "";
};

export const validateState = (state: string): string => {
  if (!state.trim()) return "State is required";
  if (!/^[a-zA-Z\s]+$/.test(state)) return "State can only contain letters and spaces";

  const usStates = [
    "Alabama", "AL", "Alaska", "AK", "Arizona", "AZ", "Arkansas", "AR", "California", "CA",
    "Colorado", "CO", "Connecticut", "CT", "Delaware", "DE", "Florida", "FL", "Georgia", "GA",
    "Hawaii", "HI", "Idaho", "ID", "Illinois", "IL", "Indiana", "IN", "Iowa", "IA",
    "Kansas", "KS", "Kentucky", "KY", "Louisiana", "LA", "Maine", "ME", "Maryland", "MD",
    "Massachusetts", "MA", "Michigan", "MI", "Minnesota", "MN", "Mississippi", "MS", "Missouri", "MO",
    "Montana", "MT", "Nebraska", "NE", "Nevada", "NV", "New Hampshire", "NH", "New Jersey", "NJ",
    "New Mexico", "NM", "New York", "NY", "North Carolina", "NC", "North Dakota", "ND", "Ohio", "OH",
    "Oklahoma", "OK", "Oregon", "OR", "Pennsylvania", "PA", "Rhode Island", "RI", "South Carolina", "SC",
    "South Dakota", "SD", "Tennessee", "TN", "Texas", "TX", "Utah", "UT", "Vermont", "VT",
    "Virginia", "VA", "Washington", "WA", "West Virginia", "WV", "Wisconsin", "WI", "Wyoming", "WY",
    "District of Columbia", "DC",
  ];

  const normalizedState = state.trim();
  if (!usStates.some((validState) => validState.toLowerCase() === normalizedState.toLowerCase())) {
    return "Please enter a valid US state (e.g., California or CA)";
  }
  return "";
};

export const validateZip = (zip: string): string => {
  if (!zip.trim()) return "ZIP code is required";
  const zipRegex = /^\d{5}(-\d{4})?$/;
  if (!zipRegex.test(zip)) {
    return "Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)";
  }

  const zipNumber = Number.parseInt(zip.split("-")[0], 10);
  if (zipNumber < 1000 || zipNumber > 99999) {
    return "Please enter a valid US ZIP code (10000-99999)";
  }
  return "";
};

export const formatPhoneNumber = (value: string): string => value.replaceAll(/\D/g, "");

export const formatDateForInput = (dateStr: string | null): string => {
  if (!dateStr) return "";
  
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle ISO date strings (YYYY-MM-DDTHH:mm:ss.sssZ or YYYY-MM-DDTHH:mm:ssZ)
  // Extract just the date part to avoid timezone issues
  const isoDateRegex = /^(\d{4}-\d{2}-\d{2})/;
  const isoDateMatch = isoDateRegex.exec(dateStr);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }
  
  // Try to parse other formats manually to avoid timezone issues
  const parts = dateStr.split(/[-/]/);
  if (parts.length === 3) {
    let year: number, month: number, day: number;
    // Check if it's MM/DD/YYYY or YYYY-MM-DD
    if (parts[0].length === 4) {
      // YYYY-MM-DD or YYYY/MM/DD
      year = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      day = Number.parseInt(parts[2], 10);
    } else {
      // MM/DD/YYYY
      month = Number.parseInt(parts[0], 10);
      day = Number.parseInt(parts[1], 10);
      year = Number.parseInt(parts[2], 10);
    }
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  
  // Fallback: try Date constructor but use local date components to avoid timezone shift
  const date = new Date(dateStr);
  if (!Number.isNaN(date.getTime())) {
    // Use local date methods to preserve the date without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  
  return "";
};

